
import { Box, Chip, Stack, Typography } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { formatDate, labelSx, sectionTitleSx, valueSx } from "./styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clean = (value: any) => {
    if (value === null || value === undefined) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined" || lower === "n/a" || lower === "na") return "";
    return trimmed;
};

// Convert "{start=..., end=...}" from the backend into a readable range.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatPeriod = (value: any) => {
    if (!value) return "";
    if (typeof value === "object") {
        const start = clean(value.start || value.startDate);
        const end = clean(value.end || value.endDate);
        if (start && end) return `${formatDate(start)} → ${formatDate(end)}`;
        if (start) return formatDate(start);
        if (end) return formatDate(end);
        return "";
    }
    return formatDate(value);
};

const STATUS_META: Record<string, { bg: string; color: string }> = {
    finished: { bg: "#d3f0e9", color: "#2d6a4f" },
    "in-progress": { bg: "#e0f2fe", color: "#0369a1" },
    triaged: { bg: "#fff3e0", color: "#ad6800" },
    planned: { bg: "#e0e7ff", color: "#3730a3" },
    cancelled: { bg: "#fde7ef", color: "#c62828" }
};

const iconBadgeSx = {
    width: 40,
    height: 40,
    borderRadius: 2,
    bgcolor: "#d3f0e9",
    color: "#2d6a4f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EncounterSection = ({ encounter }: { encounter: any }) => {
    const status = clean(encounter?.encounterStatus);
    const klass = clean(encounter?.encounterClass);
    const type = clean(encounter?.encounterType);
    const serviceType = clean(encounter?.encounterServiceType);
    const period = formatPeriod(encounter?.encounterPeriod);
    const reason = clean(encounter?.encounterReason);
    const diagnosis = clean(encounter?.encounterDiagnosis);
    const serviceProvider = clean(encounter?.serviceProvider);

    const hasAnything =
        status || klass || type || serviceType || period || reason || diagnosis || serviceProvider;
    if (!hasAnything) return null;

    const statusMeta =
        STATUS_META[status.toLowerCase()] || { bg: "#eceef2", color: "#5b6778" };

    const details = [
        { label: "Type", value: type },
        { label: "Service Type", value: serviceType },
        { label: "Service Provider", value: serviceProvider },
        { label: "Reason", value: reason }
    ].filter((row) => row.value);

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                border: "2px solid #f0f0f0",
                bgcolor: "#ffffff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    background: "linear-gradient(180deg, #2fbf71 0%, #27a864 100%)",
                    borderRadius: "3px 0 0 3px"
                }
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: 1, mb: 2 }}>
                <Box sx={iconBadgeSx}>
                    <EventOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Encounter</Typography>
            </Stack>

            {/* Status + class chips row */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ ml: 1, mb: period || details.length > 0 ? 2 : 0 }}>
                {status ? (
                    <Chip
                        size="small"
                        label={status}
                        sx={{
                            bgcolor: statusMeta.bg,
                            color: statusMeta.color,
                            fontWeight: 800,
                            letterSpacing: 0.4,
                            textTransform: "uppercase",
                            border: `1px solid ${statusMeta.color}33`
                        }}
                    />
                ) : null}
                {klass ? (
                    <Chip
                        size="small"
                        label={klass}
                        sx={{
                            bgcolor: "#e0e7ff",
                            color: "#3730a3",
                            fontWeight: 800,
                            letterSpacing: 0.4,
                            border: "1px solid #c7d2fe"
                        }}
                    />
                ) : null}
                {period ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 0.5 }}>
                        <MedicalServicesOutlinedIcon sx={{ fontSize: 16, color: "#5b6778" }} />
                        <Typography sx={{ ...valueSx, fontSize: 13 }}>{period}</Typography>
                    </Stack>
                ) : null}
            </Stack>

            {details.length > 0 ? (
                <Box
                    sx={{
                        ml: 1,
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(2, 1fr)" }
                    }}
                >
                    {details.map((row) => (
                        <Box key={row.label}>
                            <Typography sx={labelSx}>{row.label}</Typography>
                            <Typography sx={valueSx}>{row.value}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}

            {diagnosis ? (
                <Box
                    sx={{
                        ml: 1,
                        mt: details.length > 0 ? 2 : 0,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#fffbea",
                        border: "1px solid #fde68a"
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <AssignmentOutlinedIcon sx={{ fontSize: 18, color: "#b45309" }} />
                        <Typography sx={{ ...labelSx, color: "#b45309", mb: 0 }}>Diagnosis</Typography>
                    </Stack>
                    <Typography sx={{ ...valueSx, color: "#78350f" }}>{diagnosis}</Typography>
                </Box>
            ) : null}
        </Box>
    );
};

export default EncounterSection;
